// components/Services.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Settings, Edit, CheckCircle, ArrowRight, ChevronRight, Zap } from 'lucide-react';
import { Service } from '../types';

interface ServicesProps {
  services?: Service[];
}

const Services: React.FC<ServicesProps> = ({ 
  services = [
    {
      id: 'metal-design',
      title: 'Tuotteiden suunnittelu',
      description: 'CAD-suunnittelu ja 3D-mallinnus. Prototyyppien valmistus ja tuotekehitys.',
      features: ['3D CAD-suunnittelu', 'Prototyypit', 'Muovitulostus', 'Silikonivalu', 'Materiaalioptimointi', 'Tekninen dokumentaatio'],
      icon: <Edit className="w-7 h-7" />,
      color: 'from-blue-500 to-cyan-500',
      highlightColor: 'blue',
      delay: 0
    },
    {
      id: 'manufacturing',
      title: 'Valmistus & Tuotanto',
      description: 'Metallituotteiden valmistus moderneilla menetelmillä. Pienistä sarjoista suuriin tuotantoeriin.',
      features: ['Sorvaus ja jyrsintä', 'Hitsaus', 'Pintakäsittely', 'Puhdistus', 'Mittaus ja laadunvalvonta', 'Kokoonpano'],
      icon: <Settings className="w-7 h-7" />,
      color: 'from-purple-500 to-pink-500',
      highlightColor: 'purple',
      delay: 100
    },
    {
      id: 'repair-service',
      title: 'Korjaus & Huolto',
      description: 'Luotettavaa vikadiagnostiikkaa sekä korjaus- ja huoltopalvelua teknisille tuotteille.',
      features: ['Vikadiagnostiikka', 'Korjauspalvelu', 'Määräaikaishuollot', 'Varaosapalvelu', 'Testaus ja käyttöönotto', 'Raportointi'],
      icon: <Wrench className="w-7 h-7" />,
      color: 'from-green-500 to-emerald-500',
      highlightColor: 'green',
      delay: 200
    }
  ]
}) => {
  const [activeService, setActiveService] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 35px, rgba(0,0,0,0.05) 35px, rgba(0,0,0,0.05) 70px),
                             repeating-linear-gradient(0deg, transparent, transparent 35px, rgba(0,0,0,0.05) 35px, rgba(0,0,0,0.05) 70px)`
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <Zap className="w-4 h-4" />
            Palvelumme
          </div>
          
          <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Kokonaisvaltaiset <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">ratkaisut</span>
          </h2>
          
          <p className={`text-xl text-gray-600 max-w-3xl mx-auto transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Tarjoamme kattavat palvelut teknisten tuotteiden ja komponenttien suunnittelusta valmistukseen, korjaukseen ja huoltoon.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`group relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${service.delay || index * 100}ms` }}
              onMouseEnter={() => setActiveService(service.id)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className={`relative h-full bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 overflow-hidden ${
                activeService === service.id ? 'scale-105' : 'scale-100'
              }`}>
                {/* Animated Background Gradient */}
                <div 
                  className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}
                />
                


                {/* Icon */}
                <div className="relative z-10 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {service.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300">
                  {service.title}
                </h3>
                
                <p className="relative z-10 text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="relative z-10 space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li 
                      key={idx} 
                      className={`flex items-start gap-3 text-gray-700 transition-all duration-300 ${
                        activeService === service.id 
                          ? 'translate-x-2 opacity-100' 
                          : 'translate-x-0 opacity-80'
                      }`}
                      style={{ transitionDelay: `${idx * 50}ms` }}
                    >
                      <CheckCircle className={`w-5 h-5 mt-0.5 text-${service.highlightColor}-500 flex-shrink-0`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="absolute bottom-0 left-0 w-32 h-32 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="0" cy="100" r="50" fill="url(#gradient)" />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#EC4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* Bottom CTA */}
        <div className={`text-center mt-16 transition-all duration-700 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="text-gray-600 mb-6">Haluatko kuulla lisää ominaisuuksistamme?</p>
          <button onClick={() => navigate('/palvelut')} className="group inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-full font-medium hover:shadow-xl transition-all duration-300 hover:scale-105">
            Tutustu palveluihimme
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
        {/* Process Steps */}
        <div className={`mt-20 bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-10 transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent text-center mb-8">Miten toimimme</h3>
          <div className="grid md:grid-cols-5 gap-8">
            {[
              { step: '01', title: 'Yhteydenotto', desc: 'Tarpeen ja lähtötietojen läpikäynti' },
              { step: '02', title: 'Tarvekartoitus', desc: 'Tekninen arvio ja ratkaisuvaihtoehdot' },
              { step: '03', title: 'Suunnittelu ja hyväksyntä', desc: 'CAD-suunnittelu ja toteutuksen varmistus' },
              { step: '04', title: 'Toteutus', desc: 'Valmistus, korjaus ja testaus' },
              { step: '05', title: 'Luovutus', desc: 'Valmis ratkaisu asiakkaalle' }
            ].map((item, idx) => (
              <div key={idx} className="relative text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                  <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                    {item.step}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
                {idx < 4 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-8 w-6 h-6 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;